/**
 * The Admin's devices (DEVICE-PAIRING, D561, ADR-0116) - framework-neutral view-models.
 *
 *  - **Operações › Aparelhos** (`devices`): the market's registered devices - an operations-room TV, a lobby kiosk. An operator REGISTERS one,
 *    generates its single-use PAIRING CODE (shown once, with the time it lapses in the market's zone), and types nothing else: the person at the
 *    device types the code and the device receives its own token. An operator can REVOKE a device with a reason from a closed list; the
 *    token stops working at once and the registration is terminal. Every step is in the device's history.
 *
 * The console only presents: the server authorizes every call at the market asked for. A paired device is never shown its token here (the server
 * never keeps it). Kinds, states, scopes, event types and reasons are codes shown through the vocabulary catalog; neutral: no country, market name or language.
 */
import type { DeviceEventView, DeviceView } from '@vello/api-client';
import { DEVICE_KINDS, DEVICE_REVOKE_REASONS } from '@vello/device-pairing';
import { grantsAllow, type KnownGrants } from './console-access.js';

/** What the operator may do with devices (the server re-authorizes every call). */
export function deviceAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayManage: boolean } {
  if (grants === undefined) return { mayView: false, mayManage: false };
  return {
    mayView: grantsAllow(grants, { permission: 'device:view', target: 'GEO' }),
    mayManage: grantsAllow(grants, { permission: 'device:manage', target: 'GEO' }),
  };
}

/**
 * The kind an operations-room TV registered under before D647. A TV is now a Painel TV with its own link (Operações › Painéis de TV); the kind stays in the
 * kernel (existing rows still list and revoke) but is not offered for a NEW registration.
 */
export const RETIRED_DEVICE_KIND = 'ops_tv';
/** The kinds an operator can register (the kernel's list without the retired TV kind), in the order the form offers them. */
export const REGISTERABLE_DEVICE_KINDS: readonly string[] = DEVICE_KINDS.filter((k) => k !== RETIRED_DEVICE_KIND);
/** The reasons a revocation can name (the kernel's list), in the order the form offers them. */
export const DEVICE_REVOKE_REASON_CHOICES: readonly string[] = DEVICE_REVOKE_REASONS;

export type DeviceTone = 'neutral' | 'success' | 'warning' | 'danger';
export const deviceStateTone = (state: string): DeviceTone => (state === 'paired' ? 'success' : state === 'registered' ? 'warning' : 'neutral');

export interface DeviceActions {
  /** A single-use code can be generated: the registration is still waiting to be paired. */
  readonly canIssueCode: boolean;
  /** The device can be revoked: it is not revoked yet. */
  readonly canRevoke: boolean;
  /** "Excluir" (a terminal soft delete: the device leaves the list, its history stays for the audit): only a REVOKED device, and only a manager. */
  readonly canDelete: boolean;
}

/** What the signed-in operator is offered for a device. A revoked one offers only "Excluir" (it is terminal); a paired one has no code to issue (it is paired once). */
export function deviceActions(device: DeviceView, mayManage: boolean): DeviceActions {
  return { canIssueCode: mayManage && device.state === 'registered', canRevoke: mayManage && device.state !== 'revoked', canDelete: mayManage && device.state === 'revoked' };
}

const STATE_ORDER: Readonly<Record<string, number>> = { registered: 0, paired: 1, revoked: 2 };

/** Devices in use first (waiting, then paired), the revoked last; newest registration first inside each group. */
export function sortDevices(devices: readonly DeviceView[]): readonly DeviceView[] {
  return [...devices].sort((a, b) => (STATE_ORDER[a.state] ?? 9) - (STATE_ORDER[b.state] ?? 9) || b.registeredAt.localeCompare(a.registeredAt) || a.deviceId.localeCompare(b.deviceId));
}

export function deviceCounts(devices: readonly DeviceView[]): { readonly waiting: number; readonly paired: number; readonly revoked: number } {
  return {
    waiting: devices.filter((d) => d.state === 'registered').length,
    paired: devices.filter((d) => d.state === 'paired').length,
    revoked: devices.filter((d) => d.state === 'revoked').length,
  };
}

/** The sign-in name of an operator, or the first eight characters of the account id when no name is on record. */
export function operatorLabel(operators: Readonly<Record<string, string>>, id: string): string {
  return operators[id] ?? id.slice(0, 8);
}

/** The audit actor of the device's own redemption (the server writes `device`); never an account id. */
export const DEVICE_ACTOR = 'device';

/**
 * Whole seconds left before a pairing code lapses (0 once it has), so the screen can say "valid for 14 minutes" without a clock of its own
 * (the caller passes `nowMs`). The code is single-use and the server refuses an expired one whatever this says.
 */
export function pairingCodeSecondsLeft(expiresAt: string, nowMs: number): number {
  const left = Math.ceil((Date.parse(expiresAt) - nowMs) / 1000);
  return Number.isFinite(left) && left > 0 ? left : 0;
}

/** Events newest first (the server already sends them so; a screen that merges a refresh keeps the order). */
export function sortDeviceEvents(events: readonly DeviceEventView[]): readonly DeviceEventView[] {
  return [...events].sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
}

/** Which vocabulary explains an event's `reasonCode` (the revocation reasons are labels; a refused pairing carries a refusal sentence). */
export function eventReasonKind(event: DeviceEventView): 'revoke_reason' | 'refusal' | undefined {
  if (event.reasonCode === undefined) return undefined;
  return event.type === 'revoked' ? 'revoke_reason' : 'refusal';
}
